import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

import { RolesTypes } from '@flashbacktech/flashbackclient/api';

export const ROLE_OPTIONS = [
  { label: 'USER', value: RolesTypes.OrgRoles.USER },
  { label: 'BILLING', value: RolesTypes.OrgRoles.BILLING },
  { label: 'WORKSPACES', value: RolesTypes.OrgRoles.WORKSPACES },
  { label: 'ADMINISTRATOR', value: RolesTypes.OrgRoles.ADMINISTRATORS },
  { label: 'OWNER', value: RolesTypes.OrgRoles.OWNER },
] as const;

interface MultiRoleSelectProps {
  value: number;
  onChange: (role: number) => void;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: object;
}

const MultiRoleSelect: React.FC<MultiRoleSelectProps> = ({
  value,
  onChange,
  size = 'small',
  fullWidth = true,
  sx,
}) => {
  // Convert bitmask to array of selected role values
  // Note: We can't know from bitmask alone if USER was explicitly selected,
  // so we preserve USER in selections state and only decode other roles from bitmask
  const selectionsFromBitmask = useMemo<number[]>(() => {
    if (value === 0) {
      return [0];
    }
    if (value === 254) {
      return [254];
    }
    if (value === 255) {
      return [255];
    }

    // Decode bitmask: find which regular roles are set
    const selected: number[] = [];
    for (const role of ROLE_OPTIONS) {
      if (role.value > 0 && role.value < 254) {
        if ((value & role.value) === role.value) {
          selected.push(role.value);
        }
      }
    }

    // Return sorted array (stable reference for MUI Select comparison)
    // Note: We don't add USER here - it will be preserved from current selections if present
    return selected.length > 0 ? [...selected].sort((a, b) => a - b) : [0];
  }, [value]);

  // Convert selections array to bitmask
  const selectionsToBitmask = useCallback((selections: number[]): number => {
    // If nothing selected, default to USER (0)
    if (!selections || selections.length === 0) {
      return 0;
    }

    // Filter out USER (0) to check what other roles are selected
    const rolesWithoutUser = selections.filter((v) => v !== 0);

    // If only USER (0) is selected, return 0
    if (rolesWithoutUser.length === 0) {
      return 0;
    }

    // Special roles (254, 255) - if present, return them directly
    if (rolesWithoutUser.includes(255)) {
      return 255;
    }

    if (rolesWithoutUser.includes(254)) {
      return 254;
    }

    // For regular roles, combine using bitwise OR (e.g., 1 | 2 = 3)
    // USER (0) is not included in bitmask, but can be in selections array
    return rolesWithoutUser.reduce((acc, val) => acc | val, 0);
  }, []);

  // Internal state for selections array
  // We preserve USER (0) in selections even when other roles are selected
  const [selections, setSelections] = useState<number[]>(selectionsFromBitmask);
  const previousValueRef = useRef<number>(value);
  const userWasSelectedRef = useRef<boolean>(value === 0);

  // Sync internal state when value prop changes externally
  // Preserve USER selection if it was previously selected
  useEffect(() => {
    // Only sync if value changed externally (not from our own onChange)
    if (previousValueRef.current === value) {
      return;
    }
    previousValueRef.current = value;

    const decoded = selectionsFromBitmask;
    const hasOtherRoles = decoded.some((v) => v !== 0);

    if (userWasSelectedRef.current && hasOtherRoles) {
      // Merge: keep USER and add other roles from decoded
      const merged = [0, ...decoded.filter((v) => v !== 0)];
      setSelections([...merged].sort((a, b) => a - b));
    } else if (decoded.length === 0 || (decoded.length === 1 && decoded[0] === 0)) {
      // Only USER or nothing
      setSelections([0]);
      userWasSelectedRef.current = true;
    } else {
      // Other roles without USER
      setSelections(decoded);
      userWasSelectedRef.current = false;
    }
  }, [value, selectionsFromBitmask]);

  // Handle role selection changes - simple toggle behavior
  const handleChange = useCallback(
    (event: SelectChangeEvent<number[]>) => {
      const rawValue = event.target.value;
      const newSelections: number[] =
        typeof rawValue === 'string'
          ? rawValue.split(',').map((v) => Number(v.trim()))
          : (rawValue as number[]);

      // If nothing selected, default to USER (0)
      if (!newSelections || newSelections.length === 0) {
        setSelections([0]);
        userWasSelectedRef.current = true;
        previousValueRef.current = 0;
        onChange(0);
        return;
      }

      // Track if USER is selected
      const hasUser = newSelections.includes(0);
      userWasSelectedRef.current = hasUser;

      // Just use whatever MUI gives us - simple toggle, no filtering
      const sortedSelections = [...newSelections].sort((a, b) => a - b);
      setSelections(sortedSelections);
      const newBitmask = selectionsToBitmask(sortedSelections);
      previousValueRef.current = newBitmask;
      onChange(newBitmask);
    },
    [onChange, selectionsToBitmask],
  );

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      sx={{
        mb: 2,
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          bgcolor: 'white',
          '& fieldset': { borderColor: '#D0D5DD' },
          '&:hover fieldset': { borderColor: '#98A2B3' },
          '&.Mui-focused fieldset': { borderColor: '#617BFF' },
        },
        ...sx,
      }}
    >
      <Select
        multiple
        value={selections}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) => {
          const selectedArray = Array.isArray(selected) ? selected : [selected];

          if (selectedArray.length === 0) {
            return 'USER';
          }

          const labels = ROLE_OPTIONS.filter((role) =>
            selectedArray.includes(role.value),
          ).map((role) => role.label);

          return labels.join(', ');
        }}
      >
        {ROLE_OPTIONS.map((role) => (
          <MenuItem key={role.value} value={role.value}>
            <Checkbox checked={selections.includes(role.value)} />
            <ListItemText primary={role.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiRoleSelect;

